import React from 'react';
import { Card, OverlayTrigger, Popover } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
// Option A — FA5 fallback icons (the app is on @fortawesome/* 5.12.1). When we
// upgrade to Font Awesome 6, swap the five `icon:` values below to the exact
// icons in `fa6:` — this AREA_META block is the single place to change.
import {
  faTrafficLight,
  faIndustry,
  faTruckMoving,
  faSync,
  faPlug,
} from '@fortawesome/pro-regular-svg-icons';
import './bloodwork-anemia-status.css';
import { LabsmartsGuage } from '../charts/LabsmartsGauge';
import { BloodworkAnalysis } from '../../models/BloodworkAnalysis';
import { ProbabilityCategory } from '../../models/ProbabilityCategory';
import {
  getThyroidPatternInsight,
  ThyroidPatternInsight,
  ThyroidArea,
} from '../../models/thyroid-pattern-insights.generated';
import { resolveThyroidPattern, ThyroidMarkerStates, Tri } from '../../models/ThyroidAnalysis';

const arrow_up = require('../../assets/imgs/long-arrow-up-8x13.png');
const arrow_down = require('../../assets/imgs/long-arrow-down-8x12.png');

const arrowStyle: React.CSSProperties = {
  height: '11px',
  margin: '0 3px',
  verticalAlign: 'middle',
  position: 'relative',
  top: '-0.5px',
};
const conditionTitleStyle: React.CSSProperties = { color: 'black', textAlign: 'left' };
const conditionListStyle: React.CSSProperties = { paddingLeft: 0, marginLeft: 0, listStyle: 'none' };
const bulletStyle: React.CSSProperties = { display: 'inline-block', marginRight: '3px' };
const markerStyle: React.CSSProperties = { color: 'black', textAlign: 'right' };
const sectionLabelStyle: React.CSSProperties = { textAlign: 'left', fontWeight: 600, color: 'black', marginTop: '6px' };

type Marker = { direction: 'up' | 'down'; label: string };

// Per-area likelihood band — frames each area as a likelihood of dysfunction on
// the same 4-zone palette as the gauge (green→yellow→orange→red); the icon +
// label use a darker, legible shade of the matching gauge zone (BAND_PALETTE) —
// the bright gauge colors are too light to read on the panel. `score` is the
// needle position (0–100) on the gauge:
//   • highlyLikely (red)    — a detected pattern belongs to this area.
//   • likely       (orange) — an area marker is outside the lab range, no pattern.
//   • possible     (yellow) — an area marker is outside optimal (within lab), no pattern.
//   • lessLikely   (green)  — area markers present, all within optimal.
//   • notAssessed  (gray)   — none of the area's markers were measured.
type Band = 'lessLikely' | 'possible' | 'likely' | 'highlyLikely' | 'notAssessed';

// Two palettes for the same four zones (green→red) plus an unassessed gray:
//  • GAUGE_PALETTE — the bright fills on the severity bars (the SAME colors as the
//    results-page marker bar graphs) and the legend gauge arcs.
//  • BAND_PALETTE  — darker, legible shades of those hues, used to tint the
//    likelihood word (the bright bar colors wash out as small text).
const GAUGE_PALETTE = {
  green: '#78F880',
  yellow: '#FEFA83',
  orange: '#FFD37E',
  red: '#FC6552',
  gray: '#e3e3e3',
};

const BAND_PALETTE = {
  green: '#3fa34d',
  yellow: '#c79a00', // gold-yellow
  orange: '#e3741a', // saturated orange — kept well clear of the gold above and the red below
  red: '#d8472f',
  gray: '#b7b7b7',
};

// Area icons are black; unassessed areas drop the icon to the muted
// BAND_PALETTE.gray so the row still reads as inactive.
const ICON_COLOR = '#000';

// `color` tints the legend chip; the row's likelihood label is a colored badge
// (`labelBg`/`labelFg`) styled like the MOO high/low labels — bright zone
// background with the matching dark/white text MOO uses.
const BAND_META: Record<
  Band,
  { label: string; color: string; labelBg: string; labelFg: string; score: number | null; meaning: string }
> = {
  lessLikely:   { label: 'less likely',   color: BAND_PALETTE.green,  labelBg: GAUGE_PALETTE.green,  labelFg: '#14521d', score: 25,   meaning: 'Markers tested, all within optimal.' },
  possible:     { label: 'possible',      color: BAND_PALETTE.yellow, labelBg: GAUGE_PALETTE.yellow, labelFg: '#2e2a00', score: 60,   meaning: 'A marker is outside optimal (still within lab range).' },
  likely:       { label: 'likely',        color: BAND_PALETTE.orange, labelBg: GAUGE_PALETTE.orange, labelFg: '#3d2700', score: 80,   meaning: 'A marker is outside the lab reference range.' },
  highlyLikely: { label: 'highly likely', color: BAND_PALETTE.red,    labelBg: GAUGE_PALETTE.red,    labelFg: '#fff',    score: 95,   meaning: 'A detected pattern points to this area.' },
  notAssessed:  { label: 'add markers',   color: BAND_PALETTE.gray,   labelBg: '#e6e6e6',           labelFg: '#777',    score: null, meaning: "This area's markers weren't tested yet." },
};

const BAR_HEIGHT = 14; // px — ranked-bar track/fill height

// The ranked bars double-encode severity: LENGTH = the band's score (0–100) and
// HUE = the matching gauge zone. BAND_HUE maps a band to its bright GAUGE_PALETTE
// fill (identical to the hero dial's zone colors, so a red bar means the same
// thing as the needle pointing to red).
const BAND_HUE: Record<Band, keyof typeof GAUGE_PALETTE> = {
  lessLikely: 'green',
  possible: 'yellow',
  likely: 'orange',
  highlyLikely: 'red',
  notAssessed: 'gray',
};

// Band → the same ProbabilityCategory vocabulary the glucose gauge uses, so the
// hero dial reads "… is Highly Likely (95%)" exactly like the other sections.
const BAND_CATEGORY: Record<Band, ProbabilityCategory> = {
  lessLikely: ProbabilityCategory.LESS_LIKELY,
  possible: ProbabilityCategory.POSSIBLE,
  likely: ProbabilityCategory.LIKELY,
  highlyLikely: ProbabilityCategory.HIGHLY_LIKELY,
  notAssessed: ProbabilityCategory.NA,
};

// Bar-track background. The app renders on a white report card (light-only — no
// dark theme), so the track is a light neutral with an inset hairline that keeps
// its edges legible on white and behind the lighter (yellow/green) fills.
const TRACK_BG = '#e9ecf1';
const TRACK_INSET = 'inset 0 0 0 1px rgba(0,0,0,0.06)';
// Hairline ticks at the 50/70/90 tier lines (possible/likely/highly-likely
// thresholds — the same boundaries as the dial's color arcs).
const TICK_COLOR = 'rgba(0,0,0,0.18)';
const TIER_TICKS = [50, 70, 90];

// Legend display order (least → most likely, then not-assessed).
const LEGEND_ORDER: Band[] = ['lessLikely', 'possible', 'likely', 'highlyLikely', 'notAssessed'];

const AREA_ORDER: ThyroidArea[] = ['Signaling', 'Production', 'Transport', 'Conversion', 'Utilization'];

// Markers that inform each area's gauge (spec §10.2). An area is "assessed" if
// any of its markers were measured; its band = the worst deviation among them.
const AREA_MARKERS: Record<ThyroidArea, Array<keyof BloodworkAnalysis>> = {
  Signaling: ['tsh'],
  Production: ['t4Free', 't4Total', 't3Total', 'tpoAb', 'tgAb'],
  Transport: ['tbg', 't3Uptake'],
  Conversion: ['reverseT3'],
  Utilization: ['t3Free'],
};

// ── Cross-area rules ──────────────────────────────────────────────────────────
// The per-area likelihood is otherwise computed independently, which double-
// counts a marker that is really a downstream/compensatory *consequence* of
// another area. Each rule below, when its condition holds, DEMOTES the named
// area to 'lessLikely' — the axis is functioning; the abnormality is explained
// elsewhere — so it doesn't read as its own dysfunction.
//
// The pattern's own (highly-likely) area is never suppressed (areaBand checks
// flaggedAreas first), so e.g. inappropriate high TSH (SITSH → Signaling pattern
// 80010) is unaffected — only an *appropriate* high TSH is suppressed here.
//
// This list is expected to grow; keep each rule's `because` accurate for review.
const AREA_SUPPRESSION_RULES: Array<{
  when: (s: ThyroidMarkerStates) => boolean;
  suppress: ThyroidArea;
  because: string;
}> = [
  {
    // High TSH + thyroid antibodies = autoimmune Production loss; the raised TSH
    // is the pituitary compensating, not a Signaling problem.
    when: (s) => s.tsh === 'high' && s.abPos,
    suppress: 'Signaling',
    because: 'High TSH is the pituitary compensating for autoimmune Production loss (TPOAb/TgAb high) — not a Signaling problem.',
  },
];

// Centralized area metadata. `fa6` records the exact Font Awesome 6 icon to
// switch to after the @fortawesome upgrade (see spec §10.6). `iconSize` is an
// optional per-icon optical-size override (px), retained for the FA6 swap.
const AREA_META: Record<ThyroidArea, { label: string; icon: IconDefinition; fa6: string; iconSize?: number }> = {
  Signaling: { label: 'Signaling', icon: faTrafficLight, fa6: 'traffic-light', iconSize: 23 },
  Production: { label: 'Production', icon: faIndustry, fa6: 'industry-windows' },
  Transport: { label: 'Transport', icon: faTruckMoving, fa6: 'truck-moving' },
  Conversion: { label: 'Conversion', icon: faSync, fa6: 'arrows-rotate', iconSize: 22 },
  Utilization: { label: 'Utilization', icon: faPlug, fa6: 'plug-circle-bolt' },
};

// House display order for the per-pattern marker-arrow row.
const MARKER_ROW: Array<{ key: keyof BloodworkAnalysis; label: string }> = [
  { key: 'tsh', label: 'TSH' },
  { key: 't4Free', label: 'FT4' },
  { key: 't3Free', label: 'FT3' },
  { key: 't4Total', label: 'TT4' },
  { key: 't3Total', label: 'TT3' },
  { key: 'reverseT3', label: 'rT3' },
  { key: 'tbg', label: 'TBG' },
  { key: 't3Uptake', label: 'T3U' },
  { key: 'tpoAb', label: 'TPOAb' },
  { key: 'tgAb', label: 'TgAb' },
];

// marker key → abbreviated label, derived from MARKER_ROW so an area box can
// label its own markers without a second source of truth.
const MARKER_LABEL: Record<string, string> = MARKER_ROW.reduce(
  (acc, { key, label }) => ({ ...acc, [key as string]: label }),
  {} as Record<string, string>,
);

// The noun paired with each area name across the dial, the bars, and the boxes.
// Fixed in code (not read from the sheet's Pattern Title) so the three surfaces
// never disagree — flip this one value to change the wording everywhere.
const AREA_NOUN = 'Dysfunction';
const areaTitle = (area: ThyroidArea) => `${AREA_META[area].label} ${AREA_NOUN}`;

// One area's place in the ranked-bar chart: its likelihood band and the 0–100
// score that drives both the bar length and (for the hero) the dial needle.
type RankedArea = { area: ThyroidArea; band: Band; score: number };

type Props = { bloodworkAnalysis: BloodworkAnalysis };

export class ThyroidRegulationInsights extends React.Component<Props, {}> {
  // ── Null-safe marker classification (mirrors GlucoseRegulationInsights) ─────
  markerIsHigh = (m: any) => !!(m?.isGreaterThanLab?.() || m?.isGreaterThanOptimal?.());
  markerIsLow = (m: any) =>
    !!(m?.isLessThanLab?.() || m?.isStricklyLessThanOptimal?.() || m?.isLessThanOptimal?.());
  markerHasValue = (m: any) => !!m?.hasValue?.();
  // Granularity for the area likelihood ladder: outside the lab reference range
  // vs. merely outside optimal (still within lab).
  markerOutsideLab = (m: any) => !!(m?.isGreaterThanLab?.() || m?.isLessThanLab?.());
  markerOutsideOptimal = (m: any) =>
    !!(m?.isGreaterThanOptimal?.() || m?.isLessThanOptimal?.() || m?.isStricklyLessThanOptimal?.());

  triState = (m: any): Tri => {
    if (!this.markerHasValue(m)) return 'absent';
    if (this.markerIsHigh(m)) return 'high';
    if (this.markerIsLow(m)) return 'low';
    return 'opt';
  };

  buildStates = (ba: BloodworkAnalysis): ThyroidMarkerStates => ({
    tsh: this.triState(ba.tsh),
    ft4: this.triState(ba.t4Free),
    ft3: this.triState(ba.t3Free),
    rt3: this.triState(ba.reverseT3),
    tt4: this.triState(ba.t4Total),
    tt3: this.triState(ba.t3Total),
    tbg: this.triState(ba.tbg),
    t3u: this.triState(ba.t3Uptake),
    abPos: this.markerIsHigh(ba.tpoAb) || this.markerIsHigh(ba.tgAb),
  });

  buildMarkerArrows = (ba: BloodworkAnalysis): Marker[] => {
    const out: Marker[] = [];
    for (const { key, label } of MARKER_ROW) {
      const m = (ba as any)[key];
      if (this.markerIsHigh(m)) out.push({ direction: 'up', label });
      else if (this.markerIsLow(m)) out.push({ direction: 'down', label });
    }
    return out;
  };

  // Areas whose flag is explained by a cross-area rule (see AREA_SUPPRESSION_RULES).
  suppressedAreas = (ba: BloodworkAnalysis): Set<ThyroidArea> => {
    const states = this.buildStates(ba);
    const set = new Set<ThyroidArea>();
    for (const rule of AREA_SUPPRESSION_RULES) {
      if (rule.when(states)) set.add(rule.suppress);
    }
    return set;
  };

  // ── 5-Area likelihood band ──────────────────────────────────────────────────
  // Ladder: detected pattern → highly likely; marker outside lab range → likely;
  // marker outside optimal (within lab) → possible; markers present & optimal →
  // less likely; no markers measured → not assessed. A cross-area rule can then
  // demote a possible/likely area to less-likely when its marker is explained by
  // another area (the pattern/highly-likely area itself is never suppressed).
  areaBand = (
    area: ThyroidArea,
    ba: BloodworkAnalysis,
    flaggedAreas: Set<ThyroidArea>,
    suppressed?: Set<ThyroidArea>,
  ): Band => {
    if (flaggedAreas.has(area)) return 'highlyLikely';
    const keys = AREA_MARKERS[area];
    if (!keys.some((key) => this.markerHasValue((ba as any)[key]))) return 'notAssessed';
    const raw: Band = keys.some((key) => this.markerOutsideLab((ba as any)[key]))
      ? 'likely'
      : keys.some((key) => this.markerOutsideOptimal((ba as any)[key]))
      ? 'possible'
      : 'lessLikely';
    if (suppressed?.has(area) && raw !== 'lessLikely') return 'lessLikely';
    return raw;
  };

  // ── Rendering ───────────────────────────────────────────────────────────────
  renderArrow = (direction: 'up' | 'down', label: string) => {
    const src = direction === 'up' ? arrow_up : arrow_down;
    return (
      <span style={{ whiteSpace: 'nowrap' }}>
        <img src={src} alt={direction === 'up' ? '↑' : '↓'} style={arrowStyle} />
        {label}
      </span>
    );
  };

  // Rank the five areas worst→best for the ranked-bar chart. `score` is the
  // band's needle position (0–100); not-assessed areas get score 0 and sort to
  // the bottom (sortKey −1). Sort is stable, so equal scores keep AREA_ORDER.
  rankedAreas = (ba: BloodworkAnalysis, flaggedAreas: Set<ThyroidArea>): RankedArea[] => {
    const suppressed = this.suppressedAreas(ba);
    return AREA_ORDER.map((area) => {
      const band = this.areaBand(area, ba, flaggedAreas, suppressed);
      const score = BAND_META[band].score; // number | null (null = not assessed)
      return { area, band, score: score ?? 0, sortKey: score ?? -1 };
    })
      .sort((a, b) => b.sortKey - a.sortKey)
      .map(({ area, band, score }) => ({ area, band, score }));
  };

  // ── "How to read this" legend (rendered inside the title's info Popover) ──────
  renderLikelihoodLegend = () => (
    <div style={{ textAlign: 'left' }}>
      <div style={{ fontSize: '11px', color: '#888', marginBottom: '6px' }}>
        How likely each physiological area is involved.
      </div>
      {LEGEND_ORDER.map((band, i) => {
        const meta = BAND_META[band];
        return (
          <div
            key={band}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '9px',
              padding: '5px 0',
              borderTop: i === 0 ? 'none' : '1px solid #f0f0f0',
            }}
          >
            {/* mini ranked-bar swatch — same length=score / hue=zone encoding as
                the panel's bars, so the legend reads in the same visual language */}
            <div
              style={{
                position: 'relative',
                width: '56px',
                height: '10px',
                borderRadius: '3px',
                background: TRACK_BG,
                boxShadow: TRACK_INSET,
                flexShrink: 0,
              }}
            >
              {band !== 'notAssessed' && (
                <div
                  style={{
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    bottom: 0,
                    width: `${meta.score}%`,
                    background: GAUGE_PALETTE[BAND_HUE[band]],
                    borderRadius: '3px',
                  }}
                />
              )}
            </div>
            <span
              style={{
                background: '#f5f5f5',
                borderRadius: '6px',
                padding: '3px 9px',
                fontSize: '12.5px',
                fontWeight: 700,
                color: meta.color,
                whiteSpace: 'nowrap',
                minWidth: '84px',
                textAlign: 'center',
              }}
            >
              {meta.label}
            </span>
            <span style={{ fontSize: '11.5px', color: '#555', lineHeight: 1.25 }}>{meta.meaning}</span>
          </div>
        );
      })}
    </div>
  );

  likelihoodLegendPopover = () => (
    <Popover id="thyroid-likelihood-legend" style={{ maxWidth: '340px' }}>
      <Popover.Title as="div" style={{ fontSize: '13px', fontWeight: 700, backgroundColor: '#fff', borderBottom: 'none' }}>
        5-Area Likelihood
      </Popover.Title>
      <Popover.Content>{this.renderLikelihoodLegend()}</Popover.Content>
    </Popover>
  );

  // Hero dial — the single worst (top-ranked) area, rendered with the SAME
  // LabsmartsGuage the other sections use, so it reads e.g. "Production
  // Dysfunction is / Highly Likely (95%)" with familiar formatting and identical
  // zone colors to the bars below. The area+noun title comes from `areaTitle`,
  // the same helper the boxes use, so the dial and the boxes never disagree.
  renderHeroGauge = (hero: RankedArea, ba: BloodworkAnalysis) => (
    <LabsmartsGuage
      text={`${areaTitle(hero.area)} is`}
      chartContainerId="thyroidRegulationGuage"
      bloodwork={ba.bloodwork}
      probability={hero.score}
      category={BAND_CATEGORY[hero.band]}
    />
  );

  // Ranked severity bars — all five areas on a shared left baseline, sorted
  // worst→best so the set reads as one descending shape at a glance. Bar LENGTH =
  // likelihood score and HUE = the matching gauge zone (double-encoded, so length
  // still carries the meaning if hue is lost). The hero row (top-ranked — the area
  // the dial above points to) is tinted + bolded to connect the two elements.
  renderRankedBars = (ranked: RankedArea[], heroArea: ThyroidArea) => (
    <div style={{ marginBottom: '12px' }}>
      <div style={{ textAlign: 'center', fontSize: '13px', fontWeight: 700, color: '#1a1a1a', marginBottom: '8px' }}>
        LabSmarts 5-Area Thyroid Framework&trade;
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
        {ranked.map(({ area, band, score }) => {
          const meta = BAND_META[band];
          const assessed = band !== 'notAssessed';
          const isHero = area === heroArea;
          return (
            <div
              key={area}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '4px 6px',
                borderRadius: '6px',
                background: isHero ? '#eef2f8' : 'transparent',
              }}
            >
              <FontAwesomeIcon
                icon={AREA_META[area].icon}
                style={{
                  fontSize: '14px',
                  width: '18px',
                  flexShrink: 0,
                  color: assessed ? ICON_COLOR : BAND_PALETTE.gray,
                }}
              />
              <span
                style={{
                  width: '62px',
                  flexShrink: 0,
                  fontSize: '11.5px',
                  fontWeight: isHero ? 700 : 600,
                  color: assessed ? '#333' : BAND_PALETTE.gray,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {AREA_META[area].label}
              </span>
              <div
                style={{
                  position: 'relative',
                  flex: 1,
                  height: `${BAR_HEIGHT}px`,
                  borderRadius: '4px',
                  background: TRACK_BG,
                  boxShadow: TRACK_INSET,
                }}
              >
                {assessed && (
                  <div
                    style={{
                      position: 'absolute',
                      left: 0,
                      top: 0,
                      bottom: 0,
                      width: `${score}%`,
                      minWidth: '6px',
                      background: GAUGE_PALETTE[BAND_HUE[band]],
                      borderRadius: '4px',
                    }}
                  />
                )}
                {/* tier ticks — the 50/70/90 category thresholds */}
                {TIER_TICKS.map((p) => (
                  <span
                    key={p}
                    style={{ position: 'absolute', top: 0, bottom: 0, left: `${p}%`, width: '1px', background: TICK_COLOR }}
                  />
                ))}
                {/* endpoint cap — marks exactly where the bar lands */}
                {assessed && (
                  <span
                    style={{
                      position: 'absolute',
                      left: `${score}%`,
                      top: '-1px',
                      bottom: '-1px',
                      width: '2px',
                      borderRadius: '1px',
                      transform: 'translateX(-100%)',
                      background: BAND_PALETTE[BAND_HUE[band]],
                    }}
                  />
                )}
              </div>
              <span
                style={{
                  width: '30px',
                  flexShrink: 0,
                  textAlign: 'right',
                  fontSize: '11px',
                  fontWeight: isHero ? 700 : 600,
                  color: assessed ? meta.color : BAND_PALETTE.gray,
                }}
              >
                {assessed ? `${score}%` : '—'}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );

  renderBulletSection = (label: string, items: string[]) => (
    <div style={{ textAlign: 'left' }}>
      <div style={sectionLabelStyle}>{label}</div>
      <ul style={conditionListStyle}>
        {items.map((item) => (
          <li key={item} style={{ display: 'flex', alignItems: 'flex-start' }}>
            <span style={bulletStyle}>&bull;</span>
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </div>
  );

  // The deviating markers that belong to one area (its own AREA_MARKERS), so each
  // box surfaces just the markers driving that area — together the boxes tile the
  // full pattern signature without repeating a marker across boxes.
  areaMarkerArrows = (area: ThyroidArea, ba: BloodworkAnalysis): Marker[] => {
    const out: Marker[] = [];
    for (const key of AREA_MARKERS[area]) {
      const label = MARKER_LABEL[key as string];
      if (!label) continue;
      const m = (ba as any)[key];
      if (this.markerIsHigh(m)) out.push({ direction: 'up', label });
      else if (this.markerIsLow(m)) out.push({ direction: 'down', label });
    }
    return out;
  };

  // Likelihood chip — the ProbabilityCategory word on the band's bright gauge hue
  // (red gets white text), matching the dial needle and the bar for that area.
  renderChip = (band: Band) => (
    <span
      style={{
        fontSize: '10px',
        fontWeight: 700,
        borderRadius: '10px',
        padding: '2px 8px',
        whiteSpace: 'nowrap',
        flexShrink: 0,
        background: GAUGE_PALETTE[BAND_HUE[band]],
        color: band === 'highlyLikely' ? '#fff' : '#3a3a3a',
      }}
    >
      {BAND_CATEGORY[band]}
    </span>
  );

  renderMarkerRow = (markers: Marker[]) => (
    <div style={markerStyle}>
      {markers.map((marker, index) => (
        <React.Fragment key={`${marker.direction}-${marker.label}-${index}`}>
          {index > 0 && <>,{' '}</>}
          {this.renderArrow(marker.direction, marker.label)}
        </React.Fragment>
      ))}
    </div>
  );

  // One box per elevated area. The resolved-pattern area (insight passed) shows
  // the full "Consistent with" + "Possible Root Causes" content; every other
  // area ≥50% shows the minimal box — title, likelihood chip, driving markers.
  renderAreaBox = (r: RankedArea, ba: BloodworkAnalysis, insight?: ThyroidPatternInsight) => {
    const markers = this.areaMarkerArrows(r.area, ba);
    return (
      <div className="condition-section" key={r.area}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
          <span style={{ ...conditionTitleStyle, fontWeight: 700, fontSize: '14px' }}>{areaTitle(r.area)}</span>
          {this.renderChip(r.band)}
        </div>
        {insight && insight.bullets.length > 0 && this.renderBulletSection('Consistent with:', insight.bullets)}
        {markers.length > 0 && this.renderMarkerRow(markers)}
        {insight && insight.rootCauses.length > 0 && this.renderBulletSection('Possible Root Causes:', insight.rootCauses)}
      </div>
    );
  };

  // A box for every area at or above 50% (possible / likely / highly likely),
  // most-likely first — so one resolved pattern no longer hides the other areas
  // its markers lit up. Areas below 50% and not-assessed areas get no box.
  renderAreaBoxes = (ranked: RankedArea[], ba: BloodworkAnalysis, insight?: ThyroidPatternInsight) =>
    ranked
      .filter((r) => r.score >= 50)
      .map((r) => this.renderAreaBox(r, ba, insight && insight.area === r.area ? insight : undefined));

  // Serializable view of the Thyroid Insights panel for a single test, mirroring
  // render() exactly (same gating, same 5-area bands, same resolved pattern).
  // Used by the raw-data JSON export. Returns null when the panel wouldn't show.
  static exportInsights(ba: BloodworkAnalysis): {
    areas: Array<{ area: ThyroidArea; band: Band }>;
    boxes: Array<{
      area: ThyroidArea;
      band: Band;
      title: string;
      markers: Marker[];
      categoryId?: number;
      bullets?: string[];
      rootCauses?: string[];
    }>;
    pattern?: {
      categoryId: number;
      title: string;
      bullets: string[];
      rootCauses: string[];
      area: ThyroidArea;
      markers: Marker[];
    };
  } | null {
    if (!ba?.bloodwork) return null;
    const inst = new ThyroidRegulationInsights({ bloodworkAnalysis: ba });
    const hasAnyThyroidMarker = MARKER_ROW.some(({ key }) =>
      inst.markerHasValue((ba as any)[key]),
    );
    if (!hasAnyThyroidMarker) return null;

    const states = inst.buildStates(ba);
    const categoryId = resolveThyroidPattern(states);
    const insight = categoryId != null ? getThyroidPatternInsight(categoryId) : undefined;

    const flaggedAreas = new Set<ThyroidArea>();
    if (insight) flaggedAreas.add(insight.area);

    // Mirror the panel: one box per area ≥50%, most-likely first.
    const ranked = inst.rankedAreas(ba, flaggedAreas);
    const boxes = ranked
      .filter((r) => r.score >= 50)
      .map((r) => {
        const isPatternArea = !!insight && insight.area === r.area;
        return {
          area: r.area,
          band: r.band,
          title: areaTitle(r.area),
          markers: inst.areaMarkerArrows(r.area, ba),
          ...(isPatternArea && categoryId != null
            ? { categoryId, bullets: insight!.bullets, rootCauses: insight!.rootCauses }
            : {}),
        };
      });

    const suppressed = inst.suppressedAreas(ba);
    const result: {
      areas: Array<{ area: ThyroidArea; band: Band }>;
      boxes: typeof boxes;
      pattern?: any;
    } = {
      areas: AREA_ORDER.map(area => ({ area, band: inst.areaBand(area, ba, flaggedAreas, suppressed) })),
      boxes,
    };
    if (insight && categoryId != null) {
      result.pattern = {
        categoryId,
        title: areaTitle(insight.area),
        bullets: insight.bullets,
        rootCauses: insight.rootCauses,
        area: insight.area,
        markers: inst.buildMarkerArrows(ba),
      };
    }
    return result;
  }

  render() {
    const { bloodworkAnalysis } = this.props;
    if (!bloodworkAnalysis.bloodwork) return null;
    const ba = bloodworkAnalysis;

    // Render only if at least one thyroid marker was measured.
    const hasAnyThyroidMarker = MARKER_ROW.some(({ key }) => this.markerHasValue((ba as any)[key]));
    if (!hasAnyThyroidMarker) return null;

    // Single source of truth: the same pure resolver the model uses.
    const states = this.buildStates(ba);
    const categoryId = resolveThyroidPattern(states);
    const insight = categoryId != null ? getThyroidPatternInsight(categoryId) : undefined;

    const flaggedAreas = new Set<ThyroidArea>();
    if (insight) flaggedAreas.add(insight.area);

    // Worst→best ranking drives the hero dial (top-ranked), the bars, and the
    // per-area boxes. At least one thyroid marker is measured (guard above) and
    // every marker maps to an area, so ranked[0] is always an assessed area.
    const ranked = this.rankedAreas(ba, flaggedAreas);
    const hero = ranked[0];

    return (
      <Card className="evaluationPanel" id="thyroid-regulation-insights">
        <Card.Header className="evaluationPanelHeading">
          Thyroid Insights
          <OverlayTrigger placement="bottom" overlay={this.likelihoodLegendPopover()}>
            <FontAwesomeIcon
              icon="info-circle"
              style={{ cursor: 'pointer', color: '#fff', fontSize: 14, marginLeft: 8, verticalAlign: 'middle' }}
            />
          </OverlayTrigger>
        </Card.Header>
        <Card.Body>
          <div className="anemiaStatusContainer">
            {hero.band !== 'notAssessed' && this.renderHeroGauge(hero, ba)}
            {this.renderRankedBars(ranked, hero.area)}
            {this.renderAreaBoxes(ranked, ba, insight)}
          </div>
        </Card.Body>
      </Card>
    );
  }
}
